// Maison Scarlett : bouton « Voir plus » des pages de collection (cf. snippets/load-more-button.liquid).
// Le bouton est un lien vers ?page=N : on charge cette page via la Section Rendering API, on ajoute ses
// produits à la grille, puis on remplace le bouton par celui de la page chargée (ou on le retire).
class LoadMoreButton extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('[data-ref="load-more-link"]');
    this.link?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.#onClick);
    this.abortController?.abort();
  }

  #onClick = async (event) => {
    // Laisser le navigateur gérer l'ouverture dans un nouvel onglet
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (this.link.classList.contains('is-loading')) return;

    const container = this.closest('[data-ref="main-collection-products"]');
    const grid = container?.querySelector('[data-ref="main-collection-grid"]');
    if (!grid) {
      window.location.href = this.link.href;
      return;
    }

    this.#setLoading(true);
    this.abortController = new AbortController();

    try {
      const requestUrl = new URL(this.link.href, window.location.origin);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(requestUrl, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Erreur ${response.status} au chargement de ${requestUrl}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newGrid = html.querySelector('[data-ref="main-collection-grid"]');
      if (!newGrid) throw new Error('Aucune grille de produits trouvée dans la réponse');

      const newItems = Array.from(newGrid.children);
      const isPrevious = this.dataset.direction === 'previous';

      if (isPrevious) {
        grid.prepend(...newItems);
      } else {
        grid.append(...newItems);
      }

      // L'URL reflète la dernière page chargée : un rechargement ou un partage retombe sur une vraie page
      history.replaceState(history.state, '', this.link.href);

      const newButton = html.querySelector(`load-more-button[data-direction="${this.dataset.direction}"]`);
      // Les cartes produit sont précédées de leur <script> : viser la première vraie carte
      const firstNewItem = newItems.find((item) => item.tagName !== 'SCRIPT');

      if (newButton) {
        this.replaceWith(document.importNode(newButton, true));
      } else {
        this.remove();
      }

      if (firstNewItem) {
        if (isPrevious) this.#scrollTo(firstNewItem);
        this.#focusFirstIn(firstNewItem);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      // En dernier recours, navigation classique vers la page demandée
      window.location.href = this.link.href;
    } finally {
      this.#setLoading(false);
    }
  };

  #setLoading(isLoading) {
    this.link.classList.toggle('is-loading', isLoading);
    this.link.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  // Accessibilité clavier : reprendre la navigation sur le premier produit ajouté.
  // Le lien principal de la carte est en display: contents (non focalisable), d'où la boucle.
  #focusFirstIn(element) {
    for (const candidate of element.querySelectorAll('a[href], button:not([disabled])')) {
      candidate.focus({ preventScroll: true });
      if (document.activeElement === candidate) return;
    }
  }

  #scrollTo(element) {
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const targetY = element.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({ top: Math.max(0, targetY), behavior: 'instant' });
  }
}

if (!customElements.get('load-more-button')) customElements.define('load-more-button', LoadMoreButton);
